import { ArrowLeft, ChevronDown, ChevronRight, ExternalLink, PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

export interface SessionContextViewOption {
  value: string;
  label: string;
}

interface SessionContextDetailHeaderProps {
  entityName: string;
  // When set, the entity-name breadcrumb becomes a link back to its parent
  // list (e.g. Ingestions). Omitted for panels whose parent is the TOC home,
  // where the back arrow alone carries the navigation.
  onEntityClick?: () => void;
  linkTo?: string;
  onBack: () => void;
  onCollapse: () => void;
  views: SessionContextViewOption[];
  currentView: string;
  onSetView: (view: string) => void;
}

export const SessionContextDetailHeader = ({
  entityName,
  onEntityClick,
  linkTo,
  onBack,
  onCollapse,
  views,
  currentView,
  onSetView,
}: SessionContextDetailHeaderProps) => {
  // A panel left pointing at another session's entity drops the switcher
  // rather than labeling itself with an unrelated view.
  const current = views.find((v) => v.value === currentView);

  return (
    <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={onBack}
        aria-label="Back to session context"
        className="size-8"
      >
        <ArrowLeft className="size-4" />
      </Button>

      {onEntityClick ? (
        <button
          type="button"
          onClick={onEntityClick}
          title={entityName}
          className="min-w-0 max-w-[45%] shrink-0 cursor-pointer truncate rounded-md px-1.5 py-1 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          {entityName}
        </button>
      ) : (
        <span
          className="min-w-0 max-w-[45%] shrink-0 truncate text-sm font-medium text-muted-foreground"
          title={entityName}
        >
          {entityName}
        </span>
      )}

      {linkTo && (
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <a
              href={linkTo}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${entityName} in a new tab`}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="size-3.5" />
            </a>
          </TooltipTrigger>
          <TooltipContent side="bottom">Open in new tab</TooltipContent>
        </Tooltip>
      )}

      {current && (
        <>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />

          {views.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-foreground hover:bg-accent"
                >
                  {current.label}
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {views.map((v) => (
                  <DropdownMenuItem
                    key={v.value}
                    onClick={() => onSetView(v.value)}
                    className={cn(v.value === currentView && "bg-accent font-medium text-primary")}
                  >
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="px-1.5 py-1 text-sm font-medium text-foreground">{current.label}</span>
          )}
        </>
      )}

      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCollapse}
            aria-label="Collapse session context"
            className="ml-auto size-8"
          >
            <PanelRightClose className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Toggle Context</TooltipContent>
      </Tooltip>
    </div>
  );
};
